import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { StaffDailyAttendanceItem } from "@/app/api/types";
import type {
	AttendancePeriodSlot,
	AttendancePhotoItem,
	DailyAttendance,
	PeriodSlotDisplay,
	StaffDailyAttendanceDay,
	WorkerRow,
} from "../types";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Tashkent";

// ─── Time formatting ────────────────────────────────────────────────

export function formatTime(iso: string | null): string {
	if (!iso) {
		return "—";
	}
	return dayjs.utc(iso).tz(TZ).format("HH:mm");
}

// ─── Display value getters (logic for cell rendering) ─────────────────

export function getPeriodSlotDisplay(slot: AttendancePeriodSlot | null): PeriodSlotDisplay {
	if (!slot) {
		return { time: "—", isEmpty: true, isManual: false };
	}
	return {
		time: formatTime(slot.time),
		isEmpty: false,
		isManual: slot.isManual,
		insideTerritory: slot.insideTerritory,
		photoUrl: slot.photoUrl,
	};
}

// ─── Data transformation ────────────────────────────────────────────

/** Convert StaffDailyAttendanceDay to DailyAttendance for display compatibility */
function toDailyAttendanceLike(
	day: StaffDailyAttendanceDay,
	staffId: string,
	organizationId: string
): DailyAttendance {
	return {
		id: "",
		date: day.date,
		staffId,
		organizationId,
		status: (day.status ?? "ABSENT") as DailyAttendance["status"],
		morning: day.morning,
		noon: day.noon,
		evening: day.evening,
		createdAt: "",
		updatedAt: "",
	};
}

/** Build WorkerRow[] from by-staff endpoint response */
export function buildWorkerRowsFromByStaff(
	items: StaffDailyAttendanceItem[],
	organizationName: string,
	organizationId: string
): WorkerRow[] {
	return items
		.filter((item) => item.staff != null)
		.map((item) => {
			const staff = item.staff;
			const staffId = staff.id;
			const attendanceByDate: Record<string, DailyAttendance> = {};
			for (const day of item.attendance ?? []) {
				attendanceByDate[day.date] = toDailyAttendanceLike(day, staffId, organizationId);
			}
			return {
				key: staffId,
				staffId,
				fullName: staff.fullname ?? staffId,
				position: "—",
				organizationName,
				attendanceByDate,
			};
		})
		.sort((a, b) => a.fullName.localeCompare(b.fullName));
}

const PHOTO_PERIODS = ["morning", "noon", "evening"] as const;

/** Build a single gallery item for one period slot of a day (null when no photo) */
function toPhotoItem(
	day: StaffDailyAttendanceDay,
	staffId: string,
	staffName: string,
	period: (typeof PHOTO_PERIODS)[number]
): AttendancePhotoItem | null {
	const slot = day[period];
	if (!slot?.photoUrl) {
		return null;
	}
	return {
		key: `${staffId}-${day.date}-${period}`,
		staffName,
		date: day.date,
		dateLabel: dayjs(day.date).format("DD.MM.YYYY"),
		time: formatTime(slot.time),
		period: period.toUpperCase() as AttendancePhotoItem["period"],
		photoUrl: slot.photoUrl,
		insideTerritory: slot.insideTerritory ?? null,
	};
}

/** Flatten by-staff matrix into photo gallery items (one per filled period slot) */
export function buildPhotoGalleryItems(items: StaffDailyAttendanceItem[]): AttendancePhotoItem[] {
	const photos: AttendancePhotoItem[] = [];

	for (const item of items) {
		const staffId = item.staff?.id ?? "";
		const staffName = item.staff?.fullname ?? "—";

		for (const day of item.attendance ?? []) {
			for (const period of PHOTO_PERIODS) {
				const photo = toPhotoItem(day, staffId, staffName, period);
				if (photo) {
					photos.push(photo);
				}
			}
		}
	}

	return photos.sort(
		(a, b) => b.date.localeCompare(a.date) || a.staffName.localeCompare(b.staffName)
	);
}

export function buildWorkerRowsFromAttendance(
	attendanceItems: DailyAttendance[],
	staffMap: Map<string, { fullname: string }>,
	organizationName: string
): WorkerRow[] {
	const byStaff = new Map<string, WorkerRow>();

	for (const item of attendanceItems) {
		const staffId = item.staffId ?? "unknown";
		let row = byStaff.get(staffId);
		if (!row) {
			const fullName = item.staff?.fullname ?? staffMap.get(staffId)?.fullname ?? staffId;
			row = {
				key: staffId,
				staffId,
				fullName,
				position: "—",
				organizationName,
				attendanceByDate: {},
			};
			byStaff.set(staffId, row);
		}
		row.attendanceByDate[item.date] = item;
	}

	return Array.from(byStaff.values()).sort((a, b) => a.fullName.localeCompare(b.fullName));
}
